/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */


const styles = css`
  :host {
    display: block;
  }

  label {
    color: var(--foreground, #09090b);
    display: grid;
    font-size: 0.875rem;
    font-weight: 500;
    gap: 8px;
    line-height: 1.35;
  }

  input {
    background: var(--background, transparent);
    border: 1px solid var(--input, #e4e4e7);
    border-radius: var(--radius-md, 8px);
    box-sizing: border-box;
    color: var(--foreground, #09090b);
    font: inherit;
    font-size: 1rem;
    height: 36px;
    min-width: 0;
    outline: none;
    padding: 4px 12px;
    transition:
      border-color 140ms ease,
      box-shadow 140ms ease,
      background-color 140ms ease,
      opacity 140ms ease;
    width: 100%;
  }

  input:focus {
    border-color: var(--ring, #18181b);
    box-shadow: 0 0 0 3px var(--ring-shadow, rgb(24 24 27 / 12%));
  }

  input::placeholder {
    color: var(--muted-foreground, #71717a);
  }

  input:disabled {
    background: var(--input-disabled, rgb(244 244 245 / 50%));
    cursor: not-allowed;
    opacity: 0.5;
  }

  @media (min-width: 768px) {
    input {
      font-size: 0.875rem;
    }
  }
`;

export class AppTextInput extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppTextInput)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["autocomplete","data-autocomplete","disabled","data-disabled","label","data-label","name","data-name","placeholder","data-placeholder","required","data-required","kind","data-kind","value","data-value"];

  #autocomplete = "";
  #disabled = false;
  #label = "";
  #name = "";
  #placeholder = "";
  #required = false;
  #kind = "text";
  #value = "";
  #autocompleteSet = false;
  #disabledSet = false;
  #labelSet = false;
  #nameSet = false;
  #placeholderSet = false;
  #requiredSet = false;
  #kindSet = false;
  #valueSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #attr0;
  #attrLast0;
  #attrLast1;
  #attrLast2;
  #attrLast3;
  #attrLast4;
  #input = (() => { const self = this; return { get current() { return self.#root ? self.#root.querySelector('[data-argon-ref="input"]') : null; } }; })();
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get autocomplete() {
    return this.#autocomplete;
  }

  set autocomplete(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#autocomplete, next)) return;
    this.#autocomplete = next;
    this.#autocompleteSet = true;
    this.#updateBindings('autocomplete');
  }

  get disabled() {
    return this.#disabled;
  }

  set disabled(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#disabled, next)) return;
    this.#disabled = next;
    this.#disabledSet = true;
    this.#updateBindings('disabled');
  }

  get label() {
    return this.#label;
  }

  set label(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#label, next)) return;
    this.#label = next;
    this.#labelSet = true;
    this.#updateBindings('label');
  }

  get name() {
    return this.#name;
  }

  set name(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#name, next)) return;
    this.#name = next;
    this.#nameSet = true;
    this.#updateBindings('name');
  }

  get placeholder() {
    return this.#placeholder;
  }

  set placeholder(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#placeholder, next)) return;
    this.#placeholder = next;
    this.#placeholderSet = true;
    this.#updateBindings('placeholder');
  }

  get required() {
    return this.#required;
  }

  set required(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#required, next)) return;
    this.#required = next;
    this.#requiredSet = true;
    this.#updateBindings('required');
  }

  get kind() {
    return this.#kind;
  }

  set kind(value) {
    const raw = value ?? "text";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#kind, next)) return;
    this.#kind = next;
    this.#kindSet = true;
    this.#updateBindings('kind');
  }

  get value() {
    return this.#value;
  }

  set value(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#value, next)) return;
    this.#value = next;
    this.#valueSet = true;
    this.#updateBindings('value');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-autocomplete')) this.autocomplete = this.dataset.autocomplete;
    if (this.hasAttribute('data-disabled')) this.disabled = this.dataset.disabled;
    if (this.hasAttribute('data-label')) this.label = this.dataset.label;
    if (this.hasAttribute('data-name')) this.name = this.dataset.name;
    if (this.hasAttribute('data-placeholder')) this.placeholder = this.dataset.placeholder;
    if (this.hasAttribute('data-required')) this.required = this.dataset.required;
    if (this.hasAttribute('data-kind')) this.kind = this.dataset.kind;
    if (this.hasAttribute('data-value')) this.value = this.dataset.value;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.autocomplete = props.autocomplete;
      this.disabled = props.disabled;
      this.label = props.label;
      this.name = props.name;
      this.placeholder = props.placeholder;
      this.required = props.required;
      this.kind = props.kind;
      this.value = props.value;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#autocompleteSet) this.autocomplete = this.dataset.autocomplete;
    if (!this.#disabledSet) this.disabled = this.dataset.disabled;
    if (!this.#labelSet) this.label = this.dataset.label;
    if (!this.#nameSet) this.name = this.dataset.name;
    if (!this.#placeholderSet) this.placeholder = this.dataset.placeholder;
    if (!this.#requiredSet) this.required = this.dataset.required;
    if (!this.#kindSet) this.kind = this.dataset.kind;
    if (!this.#valueSet) this.value = this.dataset.value;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    (this as HTMLElement & {
        focusControl: () => void;
    }).focusControl = () => {
        this.#input.current?.focus();
    };
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
    this.#effect0();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'autocomplete':
      case 'data-autocomplete':
        this.autocomplete = newValue;
        break;
      case 'disabled':
      case 'data-disabled':
        this.disabled = newValue;
        break;
      case 'label':
      case 'data-label':
        this.label = newValue;
        break;
      case 'name':
      case 'data-name':
        this.name = newValue;
        break;
      case 'placeholder':
      case 'data-placeholder':
        this.placeholder = newValue;
        break;
      case 'required':
      case 'data-required':
        this.required = newValue;
        break;
      case 'kind':
      case 'data-kind':
        this.kind = newValue;
        break;
      case 'value':
      case 'data-value':
        this.value = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><label><!--argon-text:0-->${this.#escText(this.#label)}<!--/argon-text:0--><input data-argon-ref="input" autocomplete="${this.#escAttr(this.#autocomplete)}" name="${this.#escAttr(this.#name)}" placeholder="${this.#escAttr(this.#placeholder)}" type="${this.#escAttr(this.#kind)}" value="${this.#escAttr(this.#value)}" data-argon="2322" data-argon-bind="0"></label>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    const el = this.#input.current;
    if (!el)
        return;
    el.toggleAttribute("disabled", this.#disabled);
    el.toggleAttribute("required", this.#required);
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'label') {
      const __argonNext = `${this.#escText(this.#label)}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'autocomplete') && this.#attr0) {
      const __argonNext = `${this.#autocomplete}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('autocomplete', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'name') && this.#attr0) {
      const __argonNext = `${this.#name}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('name', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'placeholder') && this.#attr0) {
      const __argonNext = `${this.#placeholder}`;
      if (__argonNext !== this.#attrLast2) {
        this.#attrLast2 = __argonNext;
        this.#attr0.setAttribute('placeholder', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'kind') && this.#attr0) {
      const __argonNext = `${this.#kind}`;
      if (__argonNext !== this.#attrLast3) {
        this.#attrLast3 = __argonNext;
        this.#attr0.setAttribute('type', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'value') && this.#attr0) {
      const __argonNext = `${this.#value}`;
      if (__argonNext !== this.#attrLast4) {
        this.#attrLast4 = __argonNext;
        this.#attr0.setAttribute('value', __argonNext);
      }
    }
    if (this.#hooksLive && (prop === 'disabled' || prop === 'required')) this.#effect0();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2322').forEach(el => this.#listen(el, '2322', 'input', (event: Event) => {
    this.value = (event.target as HTMLInputElement).value;
    this.dispatchEvent(new CustomEvent("value-change", { bubbles: true, composed: true, detail: { value: this.value } }));
}));
    this.#eventTargets(scope, '2322').forEach(el => this.#listen(el, '2322', 'keydown', (event: KeyboardEvent) => {
    if (event.key !== "Enter") {
        return;
    }
    this.dispatchEvent(new CustomEvent("commit", { bubbles: true, composed: true, detail: void 0 }));
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
